let audioContext = new (window.AudioContext ||
    window.webkitAudioContext)();
let source;
let gainNode;
const SILENCE_THRESHOLD = 1e-3;
const SEAM_FADE_SECONDS = 0.1;
const FADE_IN_SECONDS = 0.5;
const FADE_OUT_SECONDS = 1;
const PREVIEW_SECONDS = 1.5;
const PREVIEW_FADE_OUT_SECONDS = 0.1;
// Bumped on every play/stop so a fetch that resolves after the sound was
// stopped (or replaced) does not start a stale source.
let playId = 0;
/**
 * Count near-silent samples at the start and end of a buffer.
 * MP3s without gapless metadata usually carry ~10-20ms of encoder
 * padding, which is audible as a dropout every time a loop wraps.
 */
function findEdgeSilence(buffer) {
    const data = buffer.getChannelData(0);
    let lead = 0;
    let tail = 0;
    while (lead < data.length &&
        Math.abs(data[lead]) < SILENCE_THRESHOLD) {
        lead += 1;
    }
    while (tail < data.length &&
        Math.abs(data[data.length - 1 - tail]) < SILENCE_THRESHOLD) {
        tail += 1;
    }
    return { lead, tail };
}
/**
 * Blend the last `fade` samples into the first `fade` samples with an
 * equal-power curve, then drop the tail. The loop now ends at sample
 * n - fade, and sample 0 holds exactly what used to follow it, so the
 * wrap is continuous instead of a hard jump (the "click" at the seam).
 */
function crossfadeSeam(buffer, fadeSeconds) {
    const n = buffer.length;
    const fade = Math.min(Math.floor(buffer.sampleRate * fadeSeconds), Math.floor(n / 4));
    const out = audioContext.createBuffer(buffer.numberOfChannels, n - fade, buffer.sampleRate);
    for (let c = 0; c < buffer.numberOfChannels; c++) {
        const d = buffer.getChannelData(c);
        const o = out.getChannelData(c);
        o.set(d.subarray(0, n - fade));
        for (let i = 0; i < fade; i++) {
            const t = (i / fade) * (Math.PI / 2);
            o[i] = d[i] * Math.sin(t) + d[n - fade + i] * Math.cos(t);
        }
    }
    return out;
}
/**
 * Prepare a continuous sound (white/brown noise) for gapless looping:
 * trim the encoder padding at both edges, then crossfade the seam.
 * Not for rhythmic sounds (ticking): their trailing silence is part of
 * the tick spacing and a fade would soften the first tick's attack.
 */
function makeLoopable(buffer) {
    const { lead, tail } = findEdgeSilence(buffer);
    if (lead + tail >= buffer.length) {
        return buffer;
    }
    const n = buffer.length - lead - tail;
    const trimmed = audioContext.createBuffer(buffer.numberOfChannels, n, buffer.sampleRate);
    for (let c = 0; c < buffer.numberOfChannels; c++) {
        trimmed.copyToChannel(buffer.getChannelData(c).subarray(lead, lead + n), c);
    }
    return crossfadeSeam(trimmed, SEAM_FADE_SECONDS);
}
// Decoded (and, for seamless loops, processed) buffers by URL. Decoding a
// minute of mp3 takes a noticeable fraction of a second, so it is done once.
const bufferCache = new Map();
function getAudioBuffer(url, seamlessLoop) {
    const cached = bufferCache.get(url);
    if (cached) {
        return cached;
    }
    const loading = fetch(url)
        .then((response) => {
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status} for ${url}`);
        }
        return response.arrayBuffer();
    })
        .then((arrayBuffer) => {
        if (arrayBuffer.byteLength === 0) {
            throw new Error(`Empty audio file received for ${url}`);
        }
        return audioContext.decodeAudioData(arrayBuffer);
    })
        .then((audioBuffer) => seamlessLoop ? makeLoopable(audioBuffer) : audioBuffer);
    bufferCache.set(url, loading);
    loading.catch(() => {
        // let the next attempt retry instead of caching the failure
        bufferCache.delete(url);
    });
    return loading;
}
/**
 * Fetch and decode a sound ahead of time so a later play starts instantly.
 */
export function preloadAudio(url, seamlessLoop = false) {
    getAudioBuffer(url, seamlessLoop).catch((err) => {
        console.error(`Error preloading audio "${url}":`, err);
    });
}
export function loadAndPlayAudio(url, volume, partially, seamlessLoop = false) {
    stopAudio(partially ? PREVIEW_FADE_OUT_SECONDS : FADE_OUT_SECONDS);
    playId += 1;
    const thisPlayId = playId;
    try {
        getAudioBuffer(url, seamlessLoop)
            .then((audioBuffer) => {
            if (thisPlayId !== playId) {
                return; // stopped or replaced while loading
            }
            source = audioContext.createBufferSource();
            source.buffer = audioBuffer;
            // Create and connect GainNode
            gainNode = audioContext.createGain();
            source.connect(gainNode);
            gainNode.connect(audioContext.destination);
            const now = audioContext.currentTime;
            if (partially) {
                gainNode.gain.setValueAtTime(volume, now);
            }
            else {
                // fade in from silence
                gainNode.gain.setValueAtTime(0, now);
                gainNode.gain.linearRampToValueAtTime(volume, now + FADE_IN_SECONDS);
            }
            source.loop = partially ? false : true;
            source.start(0);
            if (partially) {
                setTimeout(() => {
                    if (thisPlayId === playId) {
                        stopAudio(PREVIEW_FADE_OUT_SECONDS);
                    }
                }, PREVIEW_SECONDS * 1000);
            }
        })
            .catch((err) => {
            console.error(`Error decoding audio data for "${url}":`, err);
        });
    }
    catch (err) {
        console.error(`Error loading and playing audio "${url}":`, err);
    }
}
/**
 * Fade the current sound out and stop it. Cancels a load that is still in
 * flight. Safe to call when nothing is playing.
 */
export function stopAudio(fadeSeconds = FADE_OUT_SECONDS) {
    playId += 1;
    if (!source) {
        return;
    }
    const stoppingSource = source;
    const stoppingGain = gainNode;
    source = null;
    gainNode = null;
    try {
        const now = audioContext.currentTime;
        stoppingGain.gain.cancelScheduledValues(now);
        stoppingGain.gain.setValueAtTime(stoppingGain.gain.value, now);
        stoppingGain.gain.linearRampToValueAtTime(0, now + fadeSeconds);
        stoppingSource.stop(now + fadeSeconds);
    }
    catch (err) {
        try {
            stoppingSource.stop();
        }
        catch (e) {
            // already stopped
        }
    }
}
